import {
  type Passage,
  type Span,
  allowedWindows,
  holds,
  occurrences,
  overlaps,
} from './article-address-passage';
import { quoteIsInSource } from './article-critique';
import { checkProseSlop, findSlopPhrases } from './slop';
import { findAbsolutePromises, findSelfAwardedVerification } from './web-readiness';

/**
 * THE GUARDS: what a model's edit may not do, decided in code.
 *
 * The first automatic fix of this system was a whole-article revise through Mora's writer ("Fix the claims"),
 * and it introduced NEW legal errors into 3 articles. A prompt that says "be careful" is not a control. So the
 * automatic fixer returns {before, after} for one quoted passage and every edit is held to these rules before it
 * touches the article. An edit that fails one is not applied and the note stays open with the reason.
 *
 *   (a) `before` is found verbatim in the article, exactly once.
 *   (b) the edit lies inside the passage the note quotes (the sentence or table row, plus at most one
 *       neighbouring sentence), keeps the line structure (a table row stays one row with the same cells), and
 *       does not change the line's Markdown marker.
 *   (c) it adds, removes or changes no section citation, statute or rule code, link URL, figure, date or
 *       passage in quotation marks, UNLESS the note is about evidence AND the edit comes with a quotation that
 *       is found, word for word, in the evidence Mora stored for this revision, and everything the edit adds
 *       is in that quotation. A link URL is never changed.
 *   (d) its length changes within bounds, and it is not a deletion.
 *   (e) it adds no banned or guarantee language, no self-awarded verification and no wording that is nowhere
 *       in the article, the note or the evidence.
 *   (f) it adds no legal-direction or actor word (mandate, require, prohibit, offset, exempt, rulemaking,
 *       Legislature, commission, statute ...) the passage did not already carry, unless the note is about evidence
 *       and the edit's verified quotation carries that word. A suggestion's own wording is NOT a licence: pilot job
 *       14efdc24 turned "Whether the Legislature or the commission goes further is not something these sources can
 *       answer" into "... will mandate further cost offsets remains an open question for future rulemakings",
 *       reversing the direction and swapping the actor, and every word of it came from the critique's suggestion.
 *       The meaning check (`article-address-meaning.ts`) is the second judge of the same question.
 */

export type EditRejectionCode =
  | 'no_change'
  | 'not_found'
  | 'ambiguous'
  | 'outside_passage'
  | 'structure'
  | 'protected_changed'
  | 'evidence_unverified'
  | 'length'
  | 'banned_language'
  | 'new_wording'
  | 'legal_terms_added'
  | 'meaning_changed'
  | 'meaning_unchecked'
  | 'overlap'
  | 'declined'
  | 'no_proposal'
  | 'would_fail_checks';

export interface ProposedEdit {
  before: string;
  after: string;
  /** For a note about evidence: an exact passage of the stored evidence that supports the change. */
  evidenceQuote?: string;
}

export interface CheckedEdit {
  ok: true;
  span: Span;
  before: string;
  after: string;
  /** Protected facts the edit changed, each with the evidence quote that backs it. Empty for a prose edit. */
  evidenceBacked: string[];
}

export type EditCheck = CheckedEdit | { ok: false; code: EditRejectionCode; why: string };

export interface EditCheckInput {
  markdown: string;
  passage: Passage;
  edit: ProposedEdit;
  /** The note is about evidence (an `evidence` finding or a claim check finding), so a fact may change with a verified quote. */
  evidenceNote: boolean;
  /** The text of every page and statute section stored for this revision. */
  evidenceTexts: readonly string[];
  /** Extra text a changed citation may be justified by (the claim check's own citation). */
  context?: readonly string[];
  /** Everything the edit may draw wording from besides the article: the note's reason and suggestion, the correction. */
  vocabulary?: readonly string[];
  /** Phrases the brand bans. */
  bannedPhrases?: readonly string[];
}

// ---------------------------------------------------------------------------------------------
// Protected facts

const MONTHS = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
];
const MONTH_ABBREVIATIONS: Record<string, string> = {
  jan: 'january',
  feb: 'february',
  mar: 'march',
  apr: 'april',
  jun: 'june',
  jul: 'july',
  aug: 'august',
  sep: 'september',
  sept: 'september',
  oct: 'october',
  nov: 'november',
  dec: 'december',
};
const WEEKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const SPELLED_NUMBERS =
  'eleven|twelve|thirteen|fourteen|fifteen|sixteen|seventeen|eighteen|nineteen|twenty|thirty|forty|fifty|sixty|seventy|eighty|ninety|hundred|thousand|million|billion|trillion';
const CODE_NAMES =
  'Tax|Government|Utilities|Local Government|Business and Commerce|Business Organizations|Water|Education|Property|Health and Safety|Natural Resources|Transportation|Finance|Insurance|Labor|Agriculture|Election|Family|Penal|Civil Practice and Remedies|Internal Revenue';

const plainQuotes = (text: string): string => text.replace(/[‘’]/g, "'").replace(/[“”]/g, '"');

const collapse = (text: string): string => text.replace(/\s+/g, ' ').trim();

/**
 * The facts of a passage an edit may not touch, as a multiset of tagged tokens. Two spellings of one fact are one
 * token ("Section 41.43(d)" and "§ 41.43(d)", "20 percent" and "20%"), so a style change is not a change of fact.
 */
export function protectedFacts(text: string): Map<string, number> {
  const facts = new Map<string, number>();
  const add = (token: string) => facts.set(token, (facts.get(token) ?? 0) + 1);
  let scan = plainQuotes(text);

  // Link targets and bare URLs: never changed, whatever the evidence says.
  scan = scan.replace(/\]\(\s*<?([^)\s>]+)[^)]*\)/g, (_all, href: string) => {
    add(`url:${href}`);
    return ']';
  });
  scan = scan.replace(/https?:\/\/[^\s)>\]]+/g, (href: string) => {
    add(`url:${href}`);
    return ' ';
  });

  // Passages in quotation marks are the source's words.
  scan = scan.replace(/"([^"\n]{3,500})"/g, (_all, quoted: string) => {
    add(`quote:${collapse(quoted).toLowerCase()}`);
    return ' ';
  });

  const cite = (number: string, subsections: string) =>
    add(`cite:§${number}${subsections.replace(/\s+/g, '').toLowerCase()}`);
  scan = scan.replace(
    /(?:§§?|\bSections?\b|\bSecs?\.)\s*(\d[\d.]*\d|\d)((?:\s*\([a-z0-9-]+\))*)/gi,
    (_all, number: string, subsections: string) => {
      cite(number, subsections);
      return ' ';
    },
  );
  scan = scan.replace(/\bSubsections?\s+\(([a-z0-9-]+)\)/gi, (_all, sub: string) => {
    add(`sub:(${sub.toLowerCase()})`);
    return ' ';
  });
  scan = scan.replace(
    /\bChapters?\s+(\d+[A-Z]?(?:\s*(?:,|and|or|&)\s*\d+[A-Z]?)*)/g,
    (_all, list: string) => {
      for (const number of list.match(/\d+[A-Z]?/g) ?? []) add(`chapter:${number.toUpperCase()}`);
      return ' ';
    },
  );
  scan = scan.replace(/\bForms?\s+((?:[A-Z]{1,3}-)?\d[\w-]*)/g, (_all, form: string) => {
    add(`form:${form.toUpperCase()}`);
    return ' ';
  });
  scan = scan.replace(new RegExp(`\\b(${CODE_NAMES})\\s+Code\\b`, 'g'), (_all, name: string) => {
    add(`code:${name.toLowerCase()} code`);
    return ' ';
  });
  scan = scan.replace(
    /\bTexas Administrative Code\b|\bTAC\b|\bU\.S\.C\.|\bC\.F\.R\.|\bIRC\b|\bTexas Constitution\b|\bTex\. Const\.|\bArticle\s+[IVX]+\b/g,
    (code: string) => {
      add(`code:${code.toLowerCase().replace(/\s+/g, ' ')}`);
      return ' ';
    },
  );

  // Dates: month and weekday names. "May" is a month only beside a day or a year.
  scan = scan.replace(
    new RegExp(`\\b(${MONTHS.filter((m) => m !== 'may').join('|')})\\b`, 'gi'),
    (month: string) => {
      add(`date:${month.toLowerCase()}`);
      return ' ';
    },
  );
  scan = scan.replace(/\bMay(?=\s*\d)/g, () => {
    add('date:may');
    return ' ';
  });
  scan = scan.replace(
    /\b(Jan|Feb|Mar|Apr|Jun|Jul|Aug|Sept?|Oct|Nov|Dec)\.?(?=\s+\d)/g,
    (_all, abbreviation: string) => {
      add(`date:${MONTH_ABBREVIATIONS[abbreviation.toLowerCase()] ?? abbreviation.toLowerCase()}`);
      return ' ';
    },
  );
  scan = scan.replace(new RegExp(`\\b(${WEEKDAYS.join('|')})\\b`, 'gi'), (day: string) => {
    add(`date:${day.toLowerCase()}`);
    return ' ';
  });

  // Figures: a number with its dollar sign, scale word or percent. Commas are not part of a number.
  scan = scan.replace(
    /(\$\s?)?(\d[\d,]*(?:\.\d+)?)(?:\s*(%|percent\b|per\s*cent\b|million\b|billion\b|trillion\b|thousand\b))?/gi,
    (_all, dollar: string | undefined, number: string, unit: string | undefined) => {
      const scale = unit
        ? /^(?:%|percent|per\s*cent)$/i.test(unit)
          ? '%'
          : unit.toLowerCase()
        : '';
      add(`fig:${dollar ? '$' : ''}${number.replace(/,/g, '')}${scale}`);
      return ' ';
    },
  );
  scan.replace(new RegExp(`\\b(${SPELLED_NUMBERS})\\b`, 'gi'), (word: string) => {
    add(`word:${word.toLowerCase()}`);
    return ' ';
  });
  return facts;
}

export interface FactDelta {
  added: string[];
  removed: string[];
}

export function factDelta(before: string, after: string): FactDelta {
  const was = protectedFacts(before);
  const is = protectedFacts(after);
  const added: string[] = [];
  const removed: string[] = [];
  for (const [token, count] of is) {
    const extra = count - (was.get(token) ?? 0);
    for (let i = 0; i < extra; i += 1) added.push(token);
  }
  for (const [token, count] of was) {
    const gone = count - (is.get(token) ?? 0);
    for (let i = 0; i < gone; i += 1) removed.push(token);
  }
  return { added, removed };
}

/** A fact token in words a person can read. */
export function describeFact(token: string): string {
  const [kind, ...rest] = token.split(':');
  const value = rest.join(':');
  switch (kind) {
    case 'url':
      return `the link ${value}`;
    case 'quote':
      return `the quoted words “${value.slice(0, 80)}”`;
    case 'cite':
      return `the citation ${value}`;
    case 'sub':
      return `the subsection ${value}`;
    case 'chapter':
      return `Chapter ${value}`;
    case 'form':
      return `Form ${value}`;
    case 'code':
      return `the code name “${value}”`;
    case 'date':
      return `the date word “${value}”`;
    case 'fig':
      return `the figure ${value}`;
    default:
      return `“${value}”`;
  }
}

/** Whether an ADDED fact appears in the evidence quotation (or the extra context) that is meant to back it. */
function factIsBacked(token: string, quote: string, context: string): boolean {
  const [kind, ...rest] = token.split(':');
  const value = rest.join(':');
  if (kind === 'url') return false;
  const haystack = plainQuotes(`${quote} ${context}`)
    .toLowerCase()
    .replace(/(\d),(?=\d)/g, '$1');
  switch (kind) {
    case 'fig': {
      const number = /\d[\d.]*\d|\d/.exec(value)?.[0];
      if (!number) return false;
      const bare = new RegExp(`(?<![\\d.])${number.replace(/\./g, '\\.')}(?![\\d])`);
      if (!bare.test(haystack)) return false;
      if (value.endsWith('%')) return /%|percent|per cent/.test(haystack);
      const scale = /(million|billion|trillion|thousand)$/.exec(value)?.[1];
      return scale ? haystack.includes(scale) : true;
    }
    case 'cite': {
      const number = /^§(\d[\d.]*\d|\d)/.exec(value)?.[1];
      return Boolean(number && haystack.includes(number));
    }
    case 'chapter':
      return new RegExp(`chapters?\\s+[^.]*\\b${value.toLowerCase()}\\b`).test(haystack);
    case 'sub':
      return haystack.includes(value);
    case 'quote':
      return haystack.includes(value);
    case 'form':
    case 'code':
    case 'date':
    case 'word':
      return haystack.includes(value.toLowerCase());
    default:
      return false;
  }
}

// ---------------------------------------------------------------------------------------------
// Language

interface LanguageSignals {
  slop: Set<string>;
  slopCounts: Map<string, number>;
  emDashes: number;
  promises: number;
  selfVerified: number;
  banned: Set<string>;
}

function languageSignals(text: string, banned: readonly string[]): LanguageSignals {
  const report = checkProseSlop(text, { emDashBudget: 0 });
  const slopCounts = new Map<string, number>();
  for (const finding of report.findings)
    if (finding.ruleId !== 'em_dash')
      slopCounts.set(finding.ruleId, (slopCounts.get(finding.ruleId) ?? 0) + 1);
  const phrases = findSlopPhrases(text, 'core').map((match) => `phrase:${match.label}`);
  const lower = text.toLowerCase();
  return {
    slop: new Set([...slopCounts.keys(), ...phrases]),
    slopCounts,
    emDashes: report.emDashes,
    promises: findAbsolutePromises(text).length,
    selfVerified: findSelfAwardedVerification(text).length,
    banned: new Set(
      banned.filter((phrase) => phrase.trim() && lower.includes(phrase.toLowerCase())),
    ),
  };
}

/** The language an edit adds that the passage did not already carry, or an empty list. */
export function addedBannedLanguage(
  before: string,
  after: string,
  banned: readonly string[] = [],
): string[] {
  const was = languageSignals(before, banned);
  const is = languageSignals(after, banned);
  const found: string[] = [];
  for (const key of is.slop) if (!was.slop.has(key)) found.push(key.replace(/^phrase:/, ''));
  if (is.emDashes > was.emDashes) found.push('an em dash');
  if (is.promises > was.promises) found.push('guarantee or absolute-promise wording');
  if (is.selfVerified > was.selfVerified) found.push('a claim that the article was verified');
  for (const phrase of is.banned)
    if (!was.banned.has(phrase)) found.push(`the banned phrase “${phrase}”`);
  return found;
}

const stems = (text: string): Set<string> =>
  new Set(
    (
      plainQuotes(text)
        .toLowerCase()
        .match(/[a-z]{6,}/g) ?? []
    ).map((word) => word.slice(0, 6)),
  );

/** A word of six letters or more is "new" when nothing the edit may draw from carries its first six letters. */
const MAX_NEW_WORDS = 2;

export function newWording(after: string, pool: readonly string[]): string[] {
  const known = stems(pool.join(' '));
  const visible = after.replace(/\]\(\s*<?[^)\s>]+[^)]*\)/g, ']').replace(/https?:\/\/\S+/g, ' ');
  const words: string[] =
    plainQuotes(visible)
      .toLowerCase()
      .match(/[a-z]{6,}/g) ?? [];
  const fresh = words.filter((word) => !known.has(word.slice(0, 6)));
  return [...new Set(fresh)];
}

// ---------------------------------------------------------------------------------------------
// Legal direction and actors

/**
 * The closed list. Each entry is one concept (a rule that matches its inflections); an edit may not bring a
 * concept in that the text it replaces did not already have. The list names who acts (a legislature, a commission,
 * an agency, a court), what the law does (mandates, requires, prohibits, permits, exempts, offsets), the direction
 * of a change (increase, reduce, shift) and what follows (liability, penalty). It is deliberately a list a person
 * can read and extend, not a model's judgement.
 */
const LEGAL_TERMS: readonly (readonly [label: string, pattern: RegExp])[] = [
  ['mandate', /\bmandat\w*/i],
  ['require', /\brequir\w*|\bmust\b|\bshall\b|\bobligat\w*/i],
  ['prohibit', /\bprohibit\w*|\bforbid\w*|\bforbidden\b|\bban(?:s|ned|ning)?\b|\bbarred\b/i],
  [
    'permit',
    /\bpermit(?:s|ted|ting)?\b|\bauthori[sz]\w*|\ballow(?:s|ed|ing)?\b|\bmay not\b|\bentitle\w*|\beligib\w*/i,
  ],
  ['offset', /\boffset\w*|\bcredit(?:s|ed)?\b|\bdeduct\w*|\babate\w*|\bwaive\w*|\brebate\w*/i],
  ['exempt', /\bexempt\w*/i],
  ['rulemaking', /\brulemak\w*|\brules? proceeding/i],
  ['legislature', /\blegislat\w*|\blawmakers?\b|\bcongress\b/i],
  ['commission', /\bcommission\w*|\bPUCT?\b|\bERCOT\b/i],
  [
    'agency',
    /\bagenc(?:y|ies)\b|\bcomptroller\b|\bappraisal district\b|\bchief appraiser\b|\bappraisal review board\b|\bcourts?\b|\bgovernor\b|\bregulator\w*/i,
  ],
  ['statute', /\bstatut\w*|\bregulation\w*|\bordinance\w*/i],
  [
    'direction',
    /\bincreas\w*|\bdecreas\w*|\breduc\w*|\blower(?:s|ed|ing)?\b|\braise[sd]?\b|\bshift(?:s|ed|ing)?\b/i,
  ],
  ['liability', /\bliab\w*|\bpenalt\w*|\bfines?\b|\bfined\b|\bsanction\w*/i],
];

const visibleWords = (text: string): string =>
  text.replace(/\]\(\s*<?[^)\s>]+[^)]*\)/g, ']').replace(/https?:\/\/\S+/g, ' ');

/** The legal concepts a text names, by label. */
export function legalTermsIn(text: string): Set<string> {
  const visible = visibleWords(text);
  return new Set(
    LEGAL_TERMS.filter(([, pattern]) => pattern.test(visible)).map(([label]) => label),
  );
}

/** The legal concepts `after` names that `before` did not. */
export function addedLegalTerms(before: string, after: string): string[] {
  const was = legalTermsIn(before);
  return [...legalTermsIn(after)].filter((label) => !was.has(label));
}

/** Of the added concepts, the ones the quotation (and the claim's own citation) does not carry. */
function unbackedLegalTerms(added: readonly string[], backing: string): string[] {
  const visible = visibleWords(backing);
  return added.filter((label) => {
    const pattern = LEGAL_TERMS.find(([name]) => name === label)?.[1];
    return !(pattern && pattern.test(visible));
  });
}

// ---------------------------------------------------------------------------------------------
// Structure and length

const LINE_PREFIX = /^(\s*(?:#{1,6}\s|[-*+]\s+|\d+[.)]\s+|>\s?))/;

const cellCount = (text: string): number => (text.match(/(?<!\\)\|/g) ?? []).length;

/** The shortest `after` and the longest, given the length of the text it replaces. */
export function lengthBounds(beforeLength: number): { min: number; max: number } {
  return {
    min: Math.max(1, Math.ceil(beforeLength * 0.4)),
    max: Math.min(2_400, Math.ceil(beforeLength * 1.6) + 60),
  };
}

// ---------------------------------------------------------------------------------------------
// The check

/**
 * One proposed edit, against the article as it stands. Every rule is evaluated and the first that fails names the
 * rejection; the edit is applied only when none does.
 */
export function checkAddressEdit(input: EditCheckInput): EditCheck {
  const { markdown, passage, edit } = input;
  const before = edit.before;
  const after = edit.after;
  const reject = (code: EditRejectionCode, why: string): EditCheck => ({ ok: false, code, why });
  if (!before.trim()) return reject('not_found', 'The edit did not say which text it replaces.');
  if (before === after) return reject('no_change', 'The edit changed nothing.');
  if (!after.trim())
    return reject(
      'length',
      'The edit deleted the passage. Removing a whole sentence is a person’s decision.',
    );

  // (a) verbatim, exactly once
  const found = occurrences(markdown, before);
  if (found === 0)
    return reject('not_found', 'The text the edit replaces is not in the article word for word.');
  if (found > 1)
    return reject(
      'ambiguous',
      'The text the edit replaces appears more than once in the article, so the place is not certain.',
    );
  const start = markdown.indexOf(before);
  const span: Span = { start, end: start + before.length };

  // (b) inside the passage, structure kept
  if (!overlaps(span, passage.quote))
    return reject('outside_passage', 'The edit does not touch the passage the note quotes.');
  if (!allowedWindows(passage).some((window) => holds(window, span)))
    return reject(
      'outside_passage',
      'The edit reaches beyond the sentence the note quotes and its one neighbouring sentence.',
    );
  if ((before.match(/\n/g) ?? []).length !== (after.match(/\n/g) ?? []).length)
    return reject(
      'structure',
      'The edit changed the number of lines, which changes the structure of the page.',
    );
  if (passage.kind === 'table_row' && cellCount(before) !== cellCount(after))
    return reject('structure', 'The edit changed the cells of a table row.');
  const lineStart = markdown.lastIndexOf('\n', start - 1) + 1;
  if (start === lineStart) {
    const was = LINE_PREFIX.exec(before)?.[1]?.replace(/\d+/, '#') ?? '';
    const is = LINE_PREFIX.exec(after)?.[1]?.replace(/\d+/, '#') ?? '';
    if (was !== is) return reject('structure', 'The edit changed the line’s Markdown marker.');
  }

  // (d) length
  const bounds = lengthBounds(before.length);
  if (after.length < bounds.min || after.length > bounds.max)
    return reject(
      'length',
      `The edit changes the passage from ${before.length} to ${after.length} characters, outside the ${bounds.min} to ${bounds.max} this fixer may make.`,
    );

  // (c) protected facts
  const delta = factDelta(before, after);
  const changed = [...delta.added, ...delta.removed];
  const evidenceBacked: string[] = [];
  if (changed.length) {
    if (changed.some((token) => token.startsWith('url:')))
      return reject(
        'protected_changed',
        `The edit changes a link (${describeFact(changed.find((token) => token.startsWith('url:'))!)}). A link is never changed automatically.`,
      );
    const listed = [...new Set(changed)].slice(0, 4).map(describeFact).join(', ');
    if (!input.evidenceNote)
      return reject(
        'protected_changed',
        `The edit changes ${listed}. A citation, code, figure, date or quoted passage changes only for a note about evidence, with a quotation from the evidence.`,
      );
    const quote = edit.evidenceQuote?.trim() ?? '';
    if (quote.length < 20)
      return reject(
        'evidence_unverified',
        `The edit changes ${listed} and gave no quotation from the stored evidence to rest on.`,
      );
    if (!input.evidenceTexts.some((text) => quoteIsInSource(quote, text)))
      return reject(
        'evidence_unverified',
        `The edit changes ${listed}, but its quotation is not found word for word in the evidence stored for this version.`,
      );
    const context = (input.context ?? []).join(' ');
    const unbacked = delta.added.filter((token) => !factIsBacked(token, quote, context));
    if (unbacked.length)
      return reject(
        'evidence_unverified',
        `The edit adds ${[...new Set(unbacked)].slice(0, 3).map(describeFact).join(', ')}, which is not in its evidence quotation.`,
      );
    for (const token of changed) evidenceBacked.push(describeFact(token));
  }

  // (e) language
  const banned = addedBannedLanguage(before, after, input.bannedPhrases);
  if (banned.length)
    return reject('banned_language', `The edit adds ${banned.slice(0, 3).join(', ')}.`);
  const pool = [markdown, ...(input.vocabulary ?? []), edit.evidenceQuote ?? ''];
  const fresh = newWording(after, pool);
  if (fresh.length > MAX_NEW_WORDS)
    return reject(
      'new_wording',
      `The edit brings in wording that is nowhere in the article, the note or the evidence (${fresh.slice(0, 4).join(', ')}).`,
    );

  // (f) legal direction and actors
  const addedTerms = addedLegalTerms(before, after);
  if (addedTerms.length) {
    const quote = edit.evidenceQuote?.trim() ?? '';
    const verified =
      input.evidenceNote &&
      quote.length >= 20 &&
      input.evidenceTexts.some((text) => quoteIsInSource(quote, text));
    const unbacked = verified
      ? unbackedLegalTerms(addedTerms, `${quote} ${(input.context ?? []).join(' ')}`)
      : addedTerms;
    if (unbacked.length)
      return reject(
        'legal_terms_added',
        `The edit brings in legal wording the passage did not have (${unbacked.slice(0, 4).join(', ')}). Saying who acts, what the law does or in which direction is a change of meaning, not of voice, and nothing verified backs it.`,
      );
  }

  return { ok: true, span, before, after, evidenceBacked };
}

// ---------------------------------------------------------------------------------------------
// Applying

export interface AppliedEdit {
  span: Span;
  after: string;
}

/**
 * The edits, applied from the end of the article to the start so every offset stays true. Two edits that share a
 * character are not both applied: the earlier in `order` wins, the other is returned as overlapping.
 */
export function applyEdits<T extends AppliedEdit>(
  markdown: string,
  edits: readonly T[],
): { markdown: string; applied: T[]; overlapping: T[] } {
  const applied: T[] = [];
  const overlapping: T[] = [];
  for (const edit of edits) {
    if (applied.some((other) => overlaps(other.span, edit.span))) overlapping.push(edit);
    else applied.push(edit);
  }
  let out = markdown;
  for (const edit of [...applied].sort((a, b) => b.span.start - a.span.start))
    out = out.slice(0, edit.span.start) + edit.after + out.slice(edit.span.end);
  return { markdown: out, applied, overlapping };
}
